<template>
  <div class="min-h-screen bg-gray-950">
    <!-- The nav's default action is "Get an API Key". This page has exactly one
         call to action, so it puts that same install button up there instead. -->
    <SiteNavBar>
      <template #action>
        <ExtensionInstallCta placement="nav" compact />
      </template>
    </SiteNavBar>

    <main ref="mainEl">
      <!-- ── Hero ─────────────────────────────────────────────────────────── -->
      <div class="relative isolate overflow-hidden">
        <HeroBackdrop variant="hero" />

        <PageContainer class="pb-16 pt-10 lg:flex lg:items-center lg:gap-16 lg:pb-24 lg:pt-16">
          <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-1">

            <BaseHeading as="h1" size="display" class="mt-6">
              Grab website
              <GradientText>color palettes</GradientText>
              straight from your browser.
            </BaseHeading>
            <p class="mt-6 text-lg leading-8 text-gray-300">
              Click the toolbar icon and you get dominant colors, CSS/HTML declarations, and an eyedropper. Click any swatch to copy.
            </p>

            <!-- The page's single call to action. -->
            <div class="mt-8">
              <ExtensionInstallCta placement="hero" />
            </div>

            <ul role="list" class="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
              <li v-for="claim in heroClaims" :key="claim" class="flex items-center gap-1.5">
                <CheckIcon class="h-4 w-4 shrink-0 text-indigo-400" aria-hidden="true" />
                {{ claim }}
              </li>
            </ul>
          </div>

          <div class="mx-auto mt-16 w-full max-w-2xl lg:mx-0 lg:mt-24 lg:max-w-none lg:flex-1">
            <ExtensionPopupPreview />
          </div>
        </PageContainer>
      </div>

      <!-- ── What it does ─────────────────────────────────────────────────── -->
      <!-- `data-analytics-section` marks a scroll milestone; see the observer in
           the script block below. The hero has none — it is on screen for every
           visitor, which is what "Extension Page Viewed" already counts. -->
      <PageContainer as="section" data-analytics-section="features" class="py-16 lg:py-24">
        <BaseHeading size="title" class="max-w-3xl">
          Everything the page in front of you is made of
        </BaseHeading>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-gray-300">
          From your tab, reads 3 local sources, ranks colors by paint coverage and labels the nearest name
        </p>

        <div class="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <ExtensionFeatureCard
            v-for="feature in features"
            :key="feature.title"
            :icon="feature.icon"
            :title="feature.title"
            :description="feature.description"
            :tag="feature.tag"
          />
        </div>
      </PageContainer>

      <!-- ── Free ─────────────────────────────────────────────────────────── -->
      <!-- The price is the pitch, so it gets a price box rather than prose: the
           left column says what "free" excludes, the card says what it includes
           and carries the same install button as everywhere else. -->
      <section data-analytics-section="pricing" class="relative isolate overflow-hidden border-t border-gray-900">
        <div class="pointer-events-none absolute inset-x-0 top-0 -z-10 transform-gpu blur-3xl" aria-hidden="true">
          <div
            class="mx-auto aspect-[1155/678] w-[60rem] bg-gradient-to-tr from-indigo-600 to-sky-300 opacity-[0.08]"
            style="clip-path: polygon(0% 0%, 100% 25%, 100% 100%, 0% 75%)"
          />
        </div>

        <PageContainer class="py-16 lg:py-24">
          <div class="lg:flex lg:items-center lg:gap-16">
            <div class="max-w-2xl lg:flex-1">
              <BaseHeading size="title">
                Free and
                <GradientText>nothing to sign up for</GradientText>
              </BaseHeading>
              <p class="mt-6 text-lg leading-8 text-gray-300">
                Install it and everything is already unlocked. There is no account to create, no email to hand over,
                no trial counting down and no paid tier to upgrade to.
              </p>

              <ul role="list" class="mt-8 flex max-w-lg flex-wrap gap-2">
                <li
                  v-for="item in freeOf"
                  :key="item"
                  class="inline-flex items-center gap-1.5 rounded-full border border-gray-800 bg-gray-900/60 px-3 py-1.5 text-sm text-gray-400"
                >
                  <CheckIcon class="h-4 w-4 shrink-0 text-gray-600" aria-hidden="true" />
                  {{ item }}
                </li>
              </ul>
            </div>

            <div class="mx-auto mt-12 w-full max-w-md lg:mx-0 lg:mt-0 lg:w-[26rem] lg:shrink-0">
              <!-- The same panel as the feature tiles, one size up: the same
                   family, read as the page's one price. -->
              <BaseCard padding="lg" class="relative overflow-hidden">
                <div class="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-indigo-500/20 blur-3xl" aria-hidden="true" />

                <div class="flex items-center gap-2 text-sm font-semibold text-indigo-400">
                  <PuzzlePieceIcon class="h-4 w-4" aria-hidden="true" />
                  colorize.design browser extension
                </div>

                <div class="mt-6 flex items-baseline gap-2">
                  <span class="text-6xl font-bold tracking-tight text-white">$0</span>
                  <span class="text-base leading-7 text-gray-400">forever</span>
                </div>
                <p class="mt-2 text-sm leading-6 text-gray-400">
                  Every feature included, on every page you open.
                </p>

                <ul role="list" class="mt-8 space-y-3 border-t border-gray-800 pt-8 text-sm leading-6 text-gray-300">
                  <li v-for="item in included" :key="item" class="flex gap-3">
                    <CheckIcon class="mt-1 h-4 w-4 shrink-0 text-indigo-400" aria-hidden="true" />
                    <span>{{ item }}</span>
                  </li>
                </ul>

                <div class="mt-8">
                  <ExtensionInstallCta placement="pricing" align="center" />
                </div>
              </BaseCard>
            </div>
          </div>
        </PageContainer>
      </section>

      <!-- ── How it works ─────────────────────────────────────────────────── -->
      <section data-analytics-section="how_it_works" class="border-t border-gray-900">
        <PageContainer class="py-16 lg:py-24">
          <div class="lg:flex lg:items-start lg:gap-16">
            <div class="lg:w-96 lg:shrink-0">
              <BaseHeading size="title">How it works</BaseHeading>
              <p class="mt-4 text-base leading-7 text-gray-400">
                Nothing runs until you click the icon. No background process, no content script on every page,
                no URL to type.
              </p>
            </div>

            <ol role="list" class="mt-10 flex-1 space-y-8 lg:mt-0">
              <ExtensionStep
                v-for="(step, index) in steps"
                :key="step.title"
                :step="index + 1"
                :title="step.title"
                :description="step.description"
              >
                <template #glyph>
                  <ExtensionStepGlyph :variant="step.glyph" />
                </template>
              </ExtensionStep>
            </ol>
          </div>
        </PageContainer>
      </section>

      <!-- ── URL hand-off ─────────────────────────────────────────────────── -->
      <section data-analytics-section="url_handoff" class="border-t border-gray-900">
        <PageContainer class="py-16 lg:py-24">
          <div class="lg:flex lg:items-start lg:gap-16">
            <div class="lg:w-96 lg:shrink-0">
              <BaseHeading size="title">
                Or extract a palette from a URL
              </BaseHeading>
              <p class="mt-4 text-base leading-7 text-gray-400">
                Nothing to install. Type an address and colorize.design pulls brand colors, logo colors, a
                homepage screenshot and the site's code colors.
              </p>
            </div>

            <div class="mt-8 max-w-xl flex-1 lg:mt-0">
              <ExtensionUrlHandoff />
            </div>
          </div>
        </PageContainer>
      </section>

      <!-- ── FAQ ──────────────────────────────────────────────────────────── -->
      <section data-analytics-section="faq" class="border-t border-gray-900">
        <PageContainer class="py-16 lg:py-24">
          <BaseHeading size="title" class="text-center">
            Frequently asked questions
          </BaseHeading>
          <div class="mx-auto mt-10 max-w-3xl">
            <FaqItem
              v-for="item in faqs"
              :key="item.question"
              :question="item.question"
              :answer="item.answer"
              @toggle="onFaqToggle(item, $event)"
            />
            <div class="border-t border-gray-800"></div>
          </div>
        </PageContainer>
      </section>

      <!-- ── Closing install ──────────────────────────────────────────────── -->
      <section data-analytics-section="closing_cta" class="border-t border-gray-900">
        <PageContainer narrow class="py-20 text-center">
          <BaseHeading size="title">
            Copy any website's colors, without leaving the page
          </BaseHeading>
          <p class="mx-auto mt-4 max-w-xl text-lg leading-8 text-gray-300">
            One click on the toolbar icon, one click on a swatch, and the hex is on your clipboard.
          </p>
          <div class="mt-8 flex justify-center">
            <ExtensionInstallCta placement="footer" align="center" />
          </div>
        </PageContainer>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<script setup>
import {
  ArrowDownTrayIcon,
  CodeBracketIcon,
  EyeDropperIcon,
  PhotoIcon,
  PuzzlePieceIcon,
  RectangleGroupIcon,
  SwatchIcon,
} from '@heroicons/vue/24/outline'
import { CheckIcon } from '@heroicons/vue/20/solid'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { setClarityTag, trackEvent } from '~/composables/analytics'
import { useBrowserDetect } from '~/composables/useBrowserDetect'
import BaseCard from '~/components/atoms/BaseCard.vue'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import GradientText from '~/components/atoms/GradientText.vue'
import HeroBackdrop from '~/components/atoms/HeroBackdrop.vue'
import PageContainer from '~/components/atoms/PageContainer.vue'
import SiteNavBar from '~/components/organisms/SiteNavBar.vue'
import SiteFooter from '~/components/organisms/SiteFooter.vue'
import ExtensionPopupPreview from '~/components/organisms/ExtensionPopupPreview.vue'
import ExtensionUrlHandoff from '~/components/organisms/ExtensionUrlHandoff.vue'
import ExtensionInstallCta from '~/components/molecules/ExtensionInstallCta.vue'
import ExtensionFeatureCard from '~/components/molecules/ExtensionFeatureCard.vue'
import ExtensionStep from '~/components/molecules/ExtensionStep.vue'
import ExtensionStepGlyph from '~/components/molecules/ExtensionStepGlyph.vue'
import FaqItem from '~/components/molecules/FaqItem.vue'
import { SITE_URL, EXTENSION_URL } from '~/utils/site'

// Deliberately not "free, no account" — the line directly under the install
// button already says that, and a claim list that repeats it wastes the row.
const heroClaims = [
  'Privacy first. Nothing leaves your browser',
  'Works on Chrome, Brave, Edge, Arc, Opera and Vivaldi.',
]

const features = [
  {
    icon: PhotoIcon,
    title: 'Screenshot colors',
    tag: 'local',
    description:
      'Dominant colors quantized from a capture of the tab: the visible area, or the whole scrollable page.',
  },
  {
    icon: CodeBracketIcon,
    title: 'Code colors',
    tag: 'local',
    description:
      'Computed styles read from the live page: backgrounds, text, borders, gradients, SVG fills and shadows.',
  },
  {
    icon: EyeDropperIcon,
    title: 'Eyedropper',
    tag: 'local',
    description:
      'Sample any pixel, even inside images, canvases and gradients the DOM never mentions. Picks are kept per site.',
  },
  {
    icon: SwatchIcon,
    title: 'One-click hex copy',
    description:
      'Every swatch is a copy button, labelled with its hex and nearest name, ranked by how much it paints.',
  },
  {
    icon: ArrowDownTrayIcon,
    title: 'Download the palette',
    description:
      'Export the palette as a file named after the site, ready for your design tool or your stylesheet.',
  },
  {
    icon: RectangleGroupIcon,
    title: 'Edit before you export',
    description:
      'Keep the swatches you want and drop the rest. What’s left is what gets copied, shared and downloaded.',
  },
]

// The price box, as data. The pills are what free *excludes* and the card is
// what it includes — deliberately not the same claim twice, and the login /
// localhost reach is one line in here rather than the section it used to be.
const freeOf = [
  'No account',
  'No sign-in',
  'No trial',
  'No credit card',
  'No usage limits',
  'No ads',
]

const included = [
  'Every feature unlocked: screenshot colors, code colors and the eyedropper',
  'Unlimited palettes, hex copies, exports and downloads',
  'Works the second it installs, with no account and no setup',
  'Extraction runs on your machine; nothing leaves your browser',
]

const steps = [
  {
    title: 'Open the page you want the colors from',
    glyph: 'page',
    description:
      'Any http or https page: a live site, a staging URL, a dashboard you are logged into, or something running on your own machine.',
  },
  {
    title: 'Click the Colorize icon',
    glyph: 'toolbar',
    description:
      'The scan runs in the tab and the palette is on screen in a few hundred milliseconds. About 20ms of that is reading a 2,500-element page; there is no network round-trip.',
  },
  {
    title: 'Click a swatch to copy its hex',
    glyph: 'swatch',
    description:
      'One click puts the value on your clipboard. Edit mode keeps the swatches you want and drops the rest.',
  },
  {
    title: 'Need more? Eyedropper and downloads',
    glyph: 'handoff',
    description:
      'Eyedropper any pixel, then download the palette as CSS, SCSS, SVG, .ase, .sketchpalette and more. Or press Generate More for the whole site.',
  },
]

const faqs = [
  {
    question: 'Which browsers is the extension available for?',
    answer:
      'It is published on the Chrome Web Store and installs on any Chromium-based browser: Chrome, Brave, Microsoft Edge, Arc, Opera and Vivaldi. Firefox and Safari use different extension stores and are not supported yet.',
  },
  {
    question: 'Is it free?',
    answer:
      'Yes. There is no account, no sign-up, no trial and no paid tier inside the extension. colorize.design itself is free to use as well.',
  },
  {
    question: 'Does it send the pages I visit anywhere?',
    answer:
      'No. The extension only looks at a tab when you open the popup on it, and the color extraction, including quantizing the screenshot of your tab, runs on your own machine. A request only leaves your browser if you press Generate More, which sends that one page’s address to colorize.design, or Download, which sends the palette’s color values to be converted into the file format you picked.',
  },
  {
    question: 'Can it read a page I have to log into?',
    answer:
      'Yes, and that is the main reason it exists. It reads the page your browser has already rendered, so a dashboard, a paywalled article, a staging site or something running on localhost all have a palette, even though no crawler could ever fetch them.',
  },
  {
    question: 'How is this different from an online color extractor?',
    answer:
      'An online extractor takes a URL and asks a server to fetch it, so it sees whatever a bot sees: the public homepage, possibly a cookie banner, and nothing that needs a session. The extension reads the live DOM and a capture of the actual tab, in whatever state you scrolled it to, with no network round-trip.',
  },
  {
    question: 'Can I pick a color out of an image or a gradient?',
    answer:
      'Yes. The eyedropper samples a pixel of the rendered page, so it reads colors out of images, canvases, video frames and gradients, none of which the DOM would tell you about. Picks are added to Custom colors and kept per site.',
  },
  {
    question: 'What formats can I export the palette in?',
    answer:
      'The whole palette downloads as a palette file, named after the site, in any of the formats colorize.design supports, from CSS variables to the binary formats design tools import. The file is built by colorize.design and saved straight to your machine.',
  },
  {
    question: 'Why does nothing happen on chrome:// pages?',
    answer:
      'Chrome forbids extensions from reading its own pages, the Web Store, the PDF viewer and other extensions’ pages. There is no palette to show there, so the popup says so rather than failing quietly. Every ordinary http or https page works.',
  },
  {
    question: 'Do I need the extension to use colorize.design?',
    answer:
      'No. The site works on its own: type any address and it extracts the palette, including brand colors, logo colors and the colors declared across the site’s code. The extension is for the pages a crawler can’t reach, and for not having to leave the page you’re reading.',
  },
]

// --- Analytics --------------------------------------------------------------
// Every call goes through trackEvent(), which fires the same logical event to
// both Clarity and GA4 (see composables/analytics.ts). The install button, the
// URL field and its examples carry their own events from inside their
// components; what lives here is the page-level picture around them — who
// arrived, how far down they read, and which question they stopped to open.
const route = useRoute()
const { browser } = useBrowserDetect()

const trackBreadcrumb = () => {
  try {
    trackEvent('Extension Page Breadcrumb (generator)', { page: route.path })
  } catch {}
}

// A question is only interesting when it is opened, and the accordion closes
// the previous panel for us — that fires a toggle with `open` false, which is
// not a second question being asked.
const onFaqToggle = (item, event) => {
  if (!event?.target?.open) return
  try {
    // The question rides along as a parameter rather than in the name: GA4 caps
    // event names at 40 characters and counts distinct names against a quota,
    // so nine questions must not become nine events.
    trackEvent('Extension Page FAQ Opened', { question: item.question, page: route.path })
  } catch {}
}

// Scroll milestones. The page is one long argument for a single button, so the
// number that matters after "did they install" is how much of the argument they
// read before deciding — which is the sections they reached, not a pixel depth.
const mainEl = ref(null)
let sectionObserver = null

const observeSections = () => {
  // Old browsers just don't report scroll depth; nothing else on the page cares.
  if (typeof IntersectionObserver !== 'function' || !mainEl.value) return

  sectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        // Each milestone is passed once per visit, so the section stops being
        // watched the moment it is reported — scrolling back up is not a
        // second read.
        sectionObserver.unobserve(entry.target)
        const section = entry.target.dataset.analyticsSection
        try {
          trackEvent(`Extension Page Section (${section})`, { section, page: route.path })
        } catch {}
      }
    },
    // Deliberately not a threshold: these sections are taller than a phone
    // viewport, so "35% of it is visible" would never become true on the
    // devices that matter. Shrinking the viewport's bottom quarter instead
    // fires when the section's heading has scrolled into the upper three
    // quarters of the screen — i.e. when someone is actually reading it —
    // whatever the section's height.
    { threshold: 0, rootMargin: '0px 0px -25% 0px' },
  )

  mainEl.value
    .querySelectorAll('[data-analytics-section]')
    .forEach((el) => sectionObserver.observe(el))
}

onMounted(async () => {
  // useBrowserDetect settles twice on Brave — its probe is a promise — so the
  // arrival event waits a task rather than filing every Brave visit as Chrome.
  await new Promise((resolve) => setTimeout(resolve, 0))

  try {
    // Clarity has no event parameters, so the same two facts are also set as
    // session tags: that is what makes "watch the sessions of people who can't
    // install this" a filter in the recordings list. GA gets them as event
    // params instead, since it can't segment on Brave, Arc or Vivaldi on its
    // own — all three report themselves as Chrome.
    setClarityTag('extension_browser', browser.value.id)
    setClarityTag('extension_installable', String(browser.value.chromium))
    trackEvent('Extension Page Viewed', {
      browser: browser.value.id,
      installable: browser.value.chromium,
      page: route.path,
    })
  } catch {}

  observeSections()
})

onBeforeUnmount(() => {
  sectionObserver?.disconnect()
  sectionObserver = null
})

// --- SEO --------------------------------------------------------------------
const canonical = `${SITE_URL}/browser-extension`
const title = 'Website Color Palette Extension for Chrome - Colorize'
const description =
  "Get any website's color palette in one click. The Colorize extension reads the screenshot, HTML and CSS colors of the page you're on, including pages behind a login, and copies any hex. Free, no account."

// SoftwareApplication is what a browser extension is in schema.org terms, and
// the FAQ is marked up separately so the answers can surface on their own. Both
// go in one <script> block; `<` is escaped for the same reason as on the brand
// pages — it would otherwise close the block early.
const jsonLd = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${canonical}#extension`,
    name: 'Colorize - Website Color Palette Extractor',
    url: canonical,
    installUrl: EXTENSION_URL,
    applicationCategory: 'BrowserApplication',
    operatingSystem: 'Chrome, Brave, Microsoft Edge, Arc, Opera, Vivaldi',
    description,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: 'Colorize', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${canonical}#faq`,
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Colorize', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Browser extension', item: canonical },
    ],
  },
]).replace(/</g, '\\u003c')

useHead({
  title,
  link: [{ rel: 'canonical', href: canonical }],
  htmlAttrs: { lang: 'en' },
  script: [{ type: 'application/ld+json', innerHTML: jsonLd }],
})

useSeoMeta({
  description,
  ogTitle: title,
  ogDescription: description,
  ogType: 'website',
  ogUrl: canonical,
  ogImage: `${SITE_URL}/og-image.jpg`,
  twitterCard: 'summary_large_image',
})
</script>
